import dayjs from "dayjs";

export function formatDateForInput(timestamp) {
    if (!timestamp) return "";

    const date = new Date(Number(timestamp));

    // Check if date is valid
    if (isNaN(date.getTime())) return "";

    // Return in YYYY-MM-DD format for HTML date inputs (UTC calendar date)
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }


/** Parse MM/DD/YYYY or YYYY/MM/DD (and dashed variants) to local Date. */
export function parseLocalDate(dateString) {
  if (!dateString) return null;
  const parts = String(dateString).trim().split(/[-/]/);
  if (parts.length !== 3) {
    const fallback = new Date(dateString);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
  let year;
  let month;
  let day;
  if (parts[0].length === 4 && /^\d{4}$/.test(parts[0])) {
    [year, month, day] = parts;
  } else {
    [month, day, year] = parts;
  }
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

/** Parse member DOB to epoch ms for sorting. Returns -1 when unknown. */
export function getMemberDobTimestamp(member) {
  const rawDob = member?.dob ?? member?.date_of_birth;
  if (rawDob == null || rawDob === "") return -1;

  if (typeof rawDob === "number" && Number.isFinite(rawDob)) {
    return rawDob;
  }

  const dobStr = String(rawDob).trim();
  if (!dobStr) return -1;

  // Epoch millisecond strings first (e.g. "-326145600000") — must run before
  // date-string parsing so leading minus is not mistaken for YYYY-MM-DD
  if (/^-?\d+$/.test(dobStr)) {
    const timestamp = parseInt(dobStr, 10);
    return Number.isFinite(timestamp) ? timestamp : -1;
  }

  if (/[-/]/.test(dobStr)) {
    const localDate = parseLocalDate(dobStr);
    if (localDate) return localDate.getTime();

    const parsed = new Date(dobStr);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

    return -1;
  }

  return -1;
}

/**
 * The age a member turns in the given year — the year less the birth year, so
 * a member whose birthday is still to come this year already shows the age
 * they will be on it. Returns null when the date of birth is unknown or the
 * member was not yet born that year.
 */
export function getMemberAgeAtYearEnd(member, year = new Date().getFullYear()) {
  const birthYear = getMemberBirthYearNumber(member);
  if (birthYear === null) return null;

  const age = year - birthYear;
  return age >= 0 ? age : null;
}

/**
 * The age a member is today — the full years since the date of birth, so a
 * birthday still to come this year is not yet counted. The date of birth is
 * read as written and compared with today's local calendar date. Returns null
 * when the date of birth is unknown or still to come.
 */
export function getMemberCurrentAge(member) {
  const birth = getMemberBirthCalendarParts(member);
  if (!birth) return null;

  const now = new Date();
  const birthdayNotReached =
    now.getMonth() < birth.month ||
    (now.getMonth() === birth.month && now.getDate() < birth.day);
  const age = now.getFullYear() - birth.year - (birthdayNotReached ? 1 : 0);

  return age >= 0 ? age : null;
}

/** Calendar year of birth as a number. Returns null when unknown. A date
 *  string is read as written, since parsing it lands on local midnight and
 *  a UTC read of that can fall on the previous day; a stored timestamp is
 *  UTC midnight, so it reads in UTC like the other date fields. */
export function getMemberBirthYearNumber(member) {
  const rawDob = member?.dob ?? member?.date_of_birth;
  if (typeof rawDob === "string" && /[-/]/.test(rawDob.trim())) {
    const year = getYearFromDateString(rawDob);
    if (year !== null) return year;
  }

  const dobMs = getMemberDobTimestamp(member);
  if (dobMs === -1) return null;

  return new Date(dobMs).getUTCFullYear();
}

/** Parse member date of death to epoch ms for sorting. Returns -1 when unknown. */
export function getMemberDeathTimestamp(member) {
  const rawDod =
    member?.date_of_death ?? member?.date_of_passing ?? member?.dateOfDeath;
  if (rawDod == null || rawDod === "") return -1;

  if (typeof rawDod === "number" && Number.isFinite(rawDod)) {
    return rawDod;
  }

  const dodStr = String(rawDod).trim();
  if (!dodStr) return -1;

  if (/^-?\d+$/.test(dodStr)) {
    const timestamp = parseInt(dodStr, 10);
    return Number.isFinite(timestamp) ? timestamp : -1;
  }

  if (/[-/]/.test(dodStr)) {
    const localDate = parseLocalDate(dodStr);
    if (localDate) return localDate.getTime();

    const parsed = new Date(dodStr);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

    return -1;
  }

  return -1;
}

/**
 * Calendar parts of a stored date value — { year, month (0–11), day } — or
 * null when unknown. A date string is read as written: parsing it lands on
 * local midnight, and a UTC read of that falls on the previous day east of
 * Greenwich (1 September becomes 31 August on an IST machine). A timestamp
 * is UTC midnight and reads in UTC like the other date fields.
 */
export function getCalendarPartsFromDateValue(raw) {
  if (raw == null || raw === "") return null;

  if (typeof raw === "string" && !/^-?\d+$/.test(raw.trim())) {
    const text = raw.trim();
    const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) {
      return {
        year: parseInt(iso[1], 10),
        month: parseInt(iso[2], 10) - 1,
        day: parseInt(iso[3], 10),
      };
    }
    const slash = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slash) {
      return {
        year: parseInt(slash[3], 10),
        month: parseInt(slash[1], 10) - 1,
        day: parseInt(slash[2], 10),
      };
    }
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
      year: parsed.getUTCFullYear(),
      month: parsed.getUTCMonth(),
      day: parsed.getUTCDate(),
    };
  }

  const timestamp =
    typeof raw === "number" ? raw : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(timestamp)) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    day: date.getUTCDate(),
  };
}

/** Calendar parts of the wedding date — see getCalendarPartsFromDateValue. */
export function getMemberWeddingCalendarParts(member) {
  return getCalendarPartsFromDateValue(
    member?.wedding_anniversary ??
      member?.weddingAnniversary ??
      member?.wedding_date ??
      member?.marriage_date
  );
}

/** Calendar parts of the date of birth — see getCalendarPartsFromDateValue. */
export function getMemberBirthCalendarParts(member) {
  return getCalendarPartsFromDateValue(member?.dob ?? member?.date_of_birth);
}

/** Calendar parts of the date of death — see getCalendarPartsFromDateValue. */
export function getMemberDeathCalendarParts(member) {
  return getCalendarPartsFromDateValue(
    member?.date_of_death ?? member?.date_of_passing ?? member?.dateOfDeath
  );
}

/** Parse member wedding date to epoch ms. Returns -1 when unknown. Reads the
 *  same shapes as the date of death: epoch ms, a numeric string of it, or a
 *  dated string (YYYY-MM-DD, MM/DD/YYYY). */
export function getMemberWeddingTimestamp(member) {
  const raw =
    member?.wedding_anniversary ??
    member?.weddingAnniversary ??
    member?.wedding_date ??
    member?.marriage_date;
  if (raw == null || raw === "") return -1;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }

  const weddingStr = String(raw).trim();
  if (!weddingStr) return -1;

  if (/^-?\d+$/.test(weddingStr)) {
    const timestamp = parseInt(weddingStr, 10);
    return Number.isFinite(timestamp) ? timestamp : -1;
  }

  if (/[-/]/.test(weddingStr)) {
    const localDate = parseLocalDate(weddingStr);
    if (localDate) return localDate.getTime();

    const parsed = new Date(weddingStr);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

    return -1;
  }

  return -1;
}

/** Numeric death year for sorting. Prefers the stored year field, then falls
 *  back to the full date-of-death timestamp. Returns null when unknown. */
export function getMemberDeathYearNumber(member) {
  if (!member) return null;

  const directYear = member?.death_year ?? member?.year_of_death;
  if (directYear != null && directYear !== "") {
    const year = parseInt(directYear, 10);
    if (Number.isFinite(year)) return year;
  }

  const timestamp = getMemberDeathTimestamp(member);
  if (timestamp === -1) return null;

  return new Date(timestamp).getUTCFullYear();
}

/**
 * The age a member was when they died: the age at death on the record where
 * the form stored one, else the full years between the date of birth and the
 * date of death. Returns null when neither is known.
 */
export function getMemberAgeAtDeath(member) {
  if (!member) return null;

  const direct =
    member?.age_at_death ?? member?.ageAtDeath ?? member?.age_of_death;
  if (direct != null && direct !== "") {
    const age = parseInt(direct, 10);
    if (Number.isFinite(age) && age >= 0) return age;
  }

  const dobMs = getMemberDobTimestamp(member);
  const deathMs = getMemberDeathTimestamp(member);
  if (dobMs === -1 || deathMs === -1 || deathMs < dobMs) return null;

  const birth = new Date(dobMs);
  const death = new Date(deathMs);
  const birthdayNotReached =
    death.getUTCMonth() < birth.getUTCMonth() ||
    (death.getUTCMonth() === birth.getUTCMonth() &&
      death.getUTCDate() < birth.getUTCDate());
  const age =
    death.getUTCFullYear() - birth.getUTCFullYear() - (birthdayNotReached ? 1 : 0);

  return age >= 0 ? age : null;
}

/**
 * Full months between the dates of birth and death, for a member who died
 * before their first birthday. Returns null when either date is unknown or
 * the death is a year or more after the birth.
 */
export function getMemberMonthsAtDeath(member) {
  const dobMs = getMemberDobTimestamp(member);
  const deathMs = getMemberDeathTimestamp(member);
  if (dobMs === -1 || deathMs === -1 || deathMs < dobMs) return null;

  return fullMonthsBetween(dobMs, deathMs);
}

/** Full months from one date to a later one; null once a year has passed. */
function fullMonthsBetween(startMs, endMs) {
  const start = new Date(startMs);
  const end = new Date(endMs);
  let months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (end.getUTCMonth() - start.getUTCMonth());
  if (end.getUTCDate() < start.getUTCDate()) months -= 1;

  return months >= 0 && months < 12 ? months : null;
}

/** Whole calendar days from one date to a later one. */
function wholeDaysBetween(startMs, endMs) {
  const start = new Date(startMs);
  const end = new Date(endMs);
  const startDay = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

  return Math.round((endDay - startDay) / 86400000);
}

/**
 * Months, then days, as the age label prints them under a year old. A bare
 * "0" is kept for a death on the day of birth; `zeroLabel` is what a span of
 * no days at all prints otherwise. Without the dates to count, it stays "0".
 */
function formatInfantAge(months, days, zeroLabel = "0") {
  if (months == null) return "0";
  if (months === 1) return "1 mo.";
  if (months > 0) return `${months} mos.`;
  if (days == null) return "0";
  if (days === 0) return zeroLabel;

  return days === 1 ? "1 day" : `${days} days`;
}

const isDeceasedMemberRecord = (member) => Number(member?.present_or_deceased) === 2;

/** Today at local midnight, so it counts against a local-midnight date of birth. */
function todayMs() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

/**
 * A member's age for display: the full years, except for a child not yet a
 * year old, whose age is the full months — "5 mos." — or, within the first
 * month, the days — "12 days". `years` is the age already known (the
 * report's, say); when it is 0 the date of birth refines it. A deceased
 * member's months are counted to the date of death (unless `toToday` asks
 * for the age they would be now), so only a death on the day of birth reads
 * "0"; a living baby born today reads "Newborn". With no date of birth it
 * stays "0". Returns null when the age is unknown.
 */
export function getMemberAgeLabel(member, years, { toToday = false } = {}) {
  if (years == null) return null;
  if (years > 0) return String(years);

  if (!toToday && isDeceasedMemberRecord(member)) {
    const atDeath = getMemberAgeAtDeathLabel(member);
    if (atDeath !== null) return atDeath;
  }

  const dobMs = getMemberDobTimestamp(member);
  const now = todayMs();
  if (dobMs === -1 || dobMs > now) return "0";

  const months = fullMonthsBetween(dobMs, now);
  return formatInfantAge(months, wholeDaysBetween(dobMs, now), "Newborn");
}

/**
 * Whole days between the dates of birth and death. Returns null when either
 * date is unknown.
 */
export function getMemberDaysAtDeath(member) {
  const dobMs = getMemberDobTimestamp(member);
  const deathMs = getMemberDeathTimestamp(member);
  if (dobMs === -1 || deathMs === -1 || deathMs < dobMs) return null;

  return wholeDaysBetween(dobMs, deathMs);
}

/**
 * Age at death for display: the full years, except for a member who died
 * before their first birthday, whose age is the full months instead — "5
 * mos." — or, within the first month, the days — "12 days" — so long as
 * both dates are on the record to count them from. A death on the day of
 * birth stays "0", as does an age of 0 with no dates to refine it. Returns
 * null when the age is unknown.
 */
export function getMemberAgeAtDeathLabel(member) {
  const years = getMemberAgeAtDeath(member);
  if (years === null) return null;
  if (years > 0) return String(years);

  return formatInfantAge(getMemberMonthsAtDeath(member), getMemberDaysAtDeath(member));
}

/** Calendar year of death for display. Returns "" when unknown. */
export function getMemberDeathYear(member) {
  const year = getMemberDeathYearNumber(member);
  return year == null ? "" : String(year);
}

/**
 * How many years a member has been deceased: this calendar year less the year
 * they died, so a 2014 death reads 12 in 2026. Returns null when the death
 * year is unknown, and never goes below 0 for a year still to come.
 */
export function getMemberYearsSinceDeath(member) {
  const year = getMemberDeathYearNumber(member);
  if (year == null) return null;

  const years = new Date().getFullYear() - year;
  return years < 0 ? 0 : years;
}

/**
 * Time since death for display: the years, as getMemberYearsSinceDeath counts
 * them, except for a death this year, which reads in months — "5 mos." — or,
 * within the first month, days — "12 days" — rather than a bare 0. Returns
 * null when the death year is unknown.
 */
export function getMemberTimeSinceDeathLabel(member) {
  const years = getMemberYearsSinceDeath(member);
  if (years === null) return null;
  if (years > 0) return String(years);

  const deathMs = getMemberDeathTimestamp(member);
  const now = todayMs();
  if (deathMs === -1 || deathMs > now) return "0";

  return formatInfantAge(
    fullMonthsBetween(deathMs, now),
    wholeDaysBetween(deathMs, now),
    "today"
  );
}

/** Within-year ordinal (month then day) from the full date of death, used to
 *  order same-year deaths from the first to die to the last. Returns null when
 *  only the year is known (no month/day precision available). */
export function getMemberDeathMonthDayOrder(member) {
  const timestamp = getMemberDeathTimestamp(member);
  if (timestamp === -1) return null;

  const date = new Date(timestamp);
  return date.getUTCMonth() * 100 + date.getUTCDate();
}

export function getYearFromDateString(dateString) {
  if (!dateString) return null;
  const parts = String(dateString).trim().split(/[-/]/);
  if (parts.length !== 3) return null;
  const yearStr = parts[0].length === 4 ? parts[0] : parts[2];
  const year = parseInt(yearStr, 10);
  return Number.isNaN(year) ? null : year;
}

export const calculateAge = (dobString, referenceDateString) => {
  if (!dobString) return "";

  const dob = parseLocalDate(dobString);
  const referenceDate = referenceDateString
    ? parseLocalDate(referenceDateString)
    : new Date();

  if (!dob || !referenceDate || dob > referenceDate) {
    return "";
  }

  const ageInMilliseconds = referenceDate - dob;
  const ageDate = new Date(ageInMilliseconds);
  const age = ageDate.getUTCFullYear() - 1970;

  return age.toString();
};

function formatUtcCalendarDate(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Years from wedding to divorce, death (if either spouse is deceased), or today. */
export function calcMarriedYears(
  weddingDate,
  {
    divorce,
    divorce_date,
    aliveStatus,
    dod,
    descendantAliveStatus,
    descendantDod,
  } = {}
) {
  if (!weddingDate) return "";
  const isDivorced = divorce == 1 || divorce === "1";
  if (isDivorced && divorce_date) {
    return calculateAge(weddingDate, divorce_date);
  }

  const endDates = [];
  const isSpouseDeceased = aliveStatus == 2 || aliveStatus === "2";
  const isDescendantDeceased =
    descendantAliveStatus == 2 || descendantAliveStatus === "2";

  if (isSpouseDeceased && dod) {
    const spouseDeath = parseLocalDate(dod);
    if (spouseDeath) endDates.push(spouseDeath);
  }

  if (isDescendantDeceased && descendantDod) {
    const descendantDeath = parseLocalDate(descendantDod);
    if (descendantDeath) endDates.push(descendantDeath);
  }

  if (endDates.length > 0) {
    const earliestEnd = endDates.reduce((min, date) =>
      date < min ? date : min
    );
    return calculateAge(weddingDate, formatUtcCalendarDate(earliestEnd));
  }

  return calculateAge(weddingDate);
}

/** Calendar years since date of death. */
export function calcYearsDeceased(dod) {
  const deathYear = getYearFromDateString(dod);
  if (deathYear == null) return "";
  const diff = new Date().getUTCFullYear() - deathYear;
  return diff >= 0 ? String(diff) : "";
}


// Helper function to validate if date is complete
export const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = dayjs(dateString);
  if (!date.isValid()) return false;
  // Check if all parts are present
  const month = date.month() + 1;
  const day = date.date();
  const year = date.year();
  return month && day && year;
};
